import { useEffect, useMemo } from 'react'
import type { AuthConfig } from '../../types'
import type { ApiAuthActionsState } from '../useApiAuthActions'
import { useGenerateAuthenticateAction } from './useGenerateAuthenticateAction'
import { useGenerateFetchAccessTokenAction } from './useGenerateFetchAccessTokenAction'
import { useGenerateGetAccessTokenAction } from './useGenerateGetAccessTokenAction'
import { useGenerateSwitchOrganizationAction } from './useGenerateSwitchOrganizationAction'

export const useGenerateActions = (config: AuthConfig): ApiAuthActionsState => {
  const { setAccessToken, getRefreshToken, setRefreshToken } = config

  const authenticate = useGenerateAuthenticateAction(config)
  const fetchAccessToken = useGenerateFetchAccessTokenAction(config)
  const getAccessToken = useGenerateGetAccessTokenAction(config)
  const switchOrganization = useGenerateSwitchOrganizationAction(config)

  // All these actions are intended to be non-reactive, permanately memoized
  // useMemo could be problematic if react garbage collects this
  // which would cause renders for all consumers of these actions
  return useMemo(() => {
    const clear = () => {
      setRefreshToken(null)
      setAccessToken(null)
    }

    const isAuthenticated = () => Boolean(getRefreshToken())

    return {
      authenticate,
      clear,
      fetchAccessToken,
      switchOrganization,
      getAccessToken,
      getRefreshToken,
      isAuthenticated,
    }
  }, [
    authenticate,
    fetchAccessToken,
    switchOrganization,
    getAccessToken,
    getRefreshToken,
    setAccessToken,
    setRefreshToken,
  ])
}
